import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Mail, Lock, Chrome } from 'lucide-react';
import { Logo } from '@/components/Header';
import { safeRedirect, withRedirect, rememberAuthRedirect } from '@/lib/authRedirect';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Frontend toggle: doit être 'true' ET le backend doit également le confirmer
const FE_DEMO_ENABLED = String(process.env.REACT_APP_ENABLE_DEMO).toLowerCase() === 'true';

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const { login, googleLogin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  // The listing the traveller was booking, if any (allowed Ridalo pages only).
  const redirect = safeRedirect(searchParams.get('redirect'));

  // SEO: Prevent indexing of login page
  useEffect(() => {
    document.title = 'Login | Ridalo';
    const metaRobots = document.querySelector('meta[name="robots"]') || document.createElement('meta');
    metaRobots.setAttribute('name', 'robots');
    metaRobots.setAttribute('content', 'noindex, nofollow');
    if (!document.querySelector('meta[name="robots"]')) {
      document.head.appendChild(metaRobots);
    }
    return () => {
      if (metaRobots.parentNode) {
        metaRobots.remove();
      }
    };
  }, []);

  useEffect(() => {
    // Défense en profondeur : les deux flags (backend + frontend) doivent être true
    if (!FE_DEMO_ENABLED) { setShowDemo(false); return; }
    axios.get(`${API}/config`)
      .then((r) => setShowDemo(Boolean(r.data?.demo_accounts_visible)))
      .catch(() => setShowDemo(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    setLoading(false);

    if (result.success) {
      navigate(redirect || '/dashboard', { replace: true }); // « Retour » must not land back on the login form
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-block mb-6">
            <Logo />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-primary mb-2">{t('auth.login.title')}</h1>
          <p className="text-base text-ink-light">{t('auth.login.subtitle')}</p>
        </div>

        <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-card border border-slate-100">
          {(error || searchParams.get('error')) && (
            <Alert variant="destructive" className="mb-6" data-testid="error-alert">
              <AlertDescription>{error ? t(error) : t('auth.login.errorFallback')}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-primary mb-2 block">{t('auth.login.emailLabel')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.login.emailPlaceholder')}
                  className="input-field pl-10"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-medium text-primary mb-2 block">{t('auth.login.passwordLabel')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('auth.login.passwordPlaceholder')}
                  className="input-field pl-10"
                  required
                  data-testid="password-input"
                />
              </div>
              <div className="mt-1.5 text-right">
                <Link
                  to="/forgot-password"
                  className="inline-flex items-center min-h-11 -my-2 text-sm text-accent hover:text-accent-hover font-medium"
                  data-testid="forgot-password-link"
                >
                  {t('auth.login.forgotPassword')}
                </Link>
              </div>
            </div>

            <Button type="submit" className="btn-primary w-full" disabled={loading} data-testid="login-submit-button">
              {loading ? t('auth.login.submitting') : t('auth.login.submit')}
            </Button>
          </form>

          <div className="my-6 relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-slate-500">{t('auth.login.orContinueWith')}</span>
            </div>
          </div>

          <Button
            type="button"
            onClick={() => { rememberAuthRedirect(redirect); googleLogin('client'); }}
            className="w-full border-2 border-slate-200 bg-white text-primary hover:bg-slate-50 rounded-full px-6 py-3 font-medium transition-colors flex items-center justify-center"
            data-testid="google-login-button"
          >
            <Chrome className="w-5 h-5 mr-2" />
            {t('auth.login.googleLogin')}
          </Button>

          <div className="mt-6 text-center">
            <p className="text-sm text-ink-light">
              {t('auth.login.noAccount')}{' '}
              <Link to={withRedirect('/register', redirect)} className="inline-flex items-center min-h-11 text-accent hover:text-accent-hover font-semibold" data-testid="register-link">
                {t('auth.login.signup')}
              </Link>
            </p>
          </div>
        </div>

        {/* Comptes de démonstration (masqués en production) */}
        {showDemo && (
        <div className="mt-6 bg-white/60 backdrop-blur-sm rounded-2xl p-5 border border-slate-100" data-testid="demo-accounts">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-2 h-2 rounded-full bg-accent animate-pulse" />
            <h3 className="text-sm font-semibold text-primary">{t('auth.login.demoTitle')}</h3>
          </div>
          <p className="text-xs text-ink-light mb-3">{t('auth.login.demoSubtitle')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <button
              type="button"
              onClick={() => { setEmail('client@ridalo.co'); setPassword('client123'); }}
              className="text-left px-3 py-2 rounded-lg bg-white border border-slate-200 hover:border-accent hover:bg-accent/5 transition-all group"
              data-testid="demo-client"
            >
              <div className="text-xs font-semibold text-primary group-hover:text-accent">{t('auth.login.demoTraveler')}</div>
              <div className="text-xs text-ink-muted truncate">client@ridalo.co</div>
            </button>
            <button
              type="button"
              onClick={() => { setEmail('demo@ridalo.co'); setPassword('demo123'); }}
              className="text-left px-3 py-2 rounded-lg bg-white border border-slate-200 hover:border-accent hover:bg-accent/5 transition-all group"
              data-testid="demo-provider"
            >
              <div className="text-xs font-semibold text-primary group-hover:text-accent">{t('auth.login.demoProvider')}</div>
              <div className="text-xs text-ink-muted truncate">demo@ridalo.co</div>
            </button>
            <button
              type="button"
              onClick={() => { setEmail('admin@ridalo.co'); setPassword('admin123'); }}
              className="text-left px-3 py-2 rounded-lg bg-white border border-slate-200 hover:border-accent hover:bg-accent/5 transition-all group"
              data-testid="demo-admin"
            >
              <div className="text-xs font-semibold text-primary group-hover:text-accent">{t('auth.login.demoAdmin')}</div>
              <div className="text-xs text-ink-muted truncate">admin@ridalo.co</div>
            </button>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};